import { actionUntilLabel, isActionRunning } from '@pulsify/shared'
import type { ListingActionDto } from '@pulsify/shared/dto'
import { Loader2 } from 'lucide-react'
import * as m from '~/paraglide/messages'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '~/shared/ui/alert-dialog'
import { Badge } from '~/shared/ui/badge'
import { Button } from '~/shared/ui/button'
import { Card } from '~/shared/ui/card'
import { actionStatusText } from '../lib/action-labels'
import { actionPushStatus } from '../lib/action-status'

interface ActionTodayCardProps {
  action: ListingActionDto
  /** Sent today, so no other action can go out before tomorrow. */
  sentToday: boolean
  locale: 'de' | 'en'
  ending: boolean
  onEnd: () => void
}

export function ActionTodayCard({
  action,
  sentToday,
  locale,
  ending,
  onEnd,
}: ActionTodayCardProps) {
  const now = new Date()
  const running = isActionRunning(action, now)

  return (
    <Card className="flex flex-col gap-4 p-6">
      <div className="flex items-center justify-between gap-3">
        <h2 className="font-semibold font-serif text-2xl text-foreground tracking-tight">
          {sentToday ? m.actionTodayHeading() : m.actionRunningHeading()}
        </h2>
        {!running && <Badge variant="secondary">{m.actionEndedBadge()}</Badge>}
      </div>
      <p className="break-words text-foreground text-lg">{action.text}</p>
      {running && (
        <p className="text-muted-foreground text-sm">
          {m.actionRunsUntil({ until: actionUntilLabel(new Date(action.endsAt), now, locale) })}
        </p>
      )}
      <p className="text-foreground text-sm">{actionStatusText(actionPushStatus(action, now))}</p>
      {running && (
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button type="button" variant="outline" className="w-fit" disabled={ending}>
              {ending && <Loader2 className="animate-spin" />}
              {m.actionEndEarly()}
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{m.actionEndConfirmTitle()}</AlertDialogTitle>
              <AlertDialogDescription>{m.actionEndConfirmBody()}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{m.cancel()}</AlertDialogCancel>
              <AlertDialogAction onClick={onEnd}>{m.actionEndEarly()}</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
      {sentToday && <p className="text-muted-foreground text-sm">{m.actionTodayDone()}</p>}
    </Card>
  )
}
